import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useLocation } from 'react-router-dom'
import { CheckCircle2, Info, XCircle } from 'lucide-react'
import { useTRPC } from '@/lib/trpc'
import type { RouterOutputs } from '@/lib/api-types'
import { formatRawTokenAmount, formatTokenAmount } from '@/lib/format'
import { noteOwnActivity, useInvalidatePortfolio } from '@/lib/portfolio'
import { SOL_MINT, USDC_MINT, USDT_MINT, tokenLogo } from '@/lib/default-tokens'
import { PlatformMark } from './platform-chip'
import { dismissNotice, useNotices } from '@/lib/notices'
import { useAnnouncedFlows, withdrawAnnounced } from '@/lib/announced-flows'
import { Ticker } from './ticker'
import { TokenIcon } from './holdings-table'

type Flow = RouterOutputs['progress']['list']['flows'][number]
type TitlePart = Flow['title'][number]
type Target = Flow['target']

// taller, with the estimate's fill along the bottom edge (John, Nov 1 2026)
const CHIP = 'relative flex h-11 shrink-0 items-center gap-2 overflow-hidden rounded-lg border border-line bg-surface-2 px-3.5'
const CORE_BY_SYMBOL: Record<string, string> = { SOL: SOL_MINT, USDC: USDC_MINT, USDT: USDT_MINT }
/** how long a finished chip stays, its bar draining right to left (John, Nov 1 2026: "let it hang in there for 3 seconds when done"; Nov 5 2026: 3.5 s) */
const FINISHED_MS = 3_500
/** a thin muted divider between the parts of a chip (John, Nov 1 2026: a muted bar, not bullet points) */
const Sep = () => (
  <span className="mx-0.5 text-muted" aria-hidden="true">
    |
  </span>
)

/** the words of a chip as the server lists them: text, a token with its logo and amount, an amount, a place's mark (John, Nov 1 2026: "CEX logos without the word"), a divider */
function Title({ parts }: { parts: TitlePart[] }) {
  return (
    <>
      {parts.map((part, i) => {
        switch (part.type) {
          case 'text':
            return <span key={i}>{part.text}</span>
          case 'sep':
            return <Sep key={i} />
          case 'place':
            return <PlatformMark key={i} id={part.place} size="size-3.5" />
          case 'amount': {
            const amount = part.amountRaw != null && part.decimals != null ? formatRawTokenAmount(part.amountRaw, part.decimals, part.symbol) : part.amountUi != null ? formatTokenAmount(part.amountUi, part.symbol) : null
            return <span key={i}>{amount ? `${amount} ${part.symbol}` : part.symbol}</span>
          }
          case 'logo':
            return <TokenIcon key={i} symbol={part.symbol} address={part.address ?? CORE_BY_SYMBOL[part.symbol]} imageUrl={tokenLogo(part.address ?? CORE_BY_SYMBOL[part.symbol], part.imageUrl ?? undefined)} size="size-4" />
          case 'token': {
            const amount =
              part.amountRaw && part.decimals !== null ? formatRawTokenAmount(part.amountRaw, part.decimals, part.symbol) : part.amountUi !== null ? formatTokenAmount(part.amountUi, part.symbol) : null
            return (
              <span key={i} className="flex items-center gap-1">
                {amount ? <span>{amount}</span> : null}
                {/* a core coin's logo by its mint; anything else brings its own image */}
                <Ticker symbol={part.symbol} address={part.address ?? CORE_BY_SYMBOL[part.symbol]} imageUrl={tokenLogo(part.address ?? CORE_BY_SYMBOL[part.symbol], part.imageUrl ?? undefined)} />
              </span>
            )
          }
        }
      })}
    </>
  )
}

/** where a chip opens: the dialog that follows the flow; a strategy opens its detail on the page the bar is on (John, Nov 2 2026) */
function hrefOf(target: Target, pathname: string) {
  switch (target.kind) {
    case 'deposit':
      return `/dashboard?action=deposit&id=${target.id}`
    case 'withdrawal':
      return `/dashboard?action=withdraw&withdrawal=${target.id}`
    case 'privateWithdrawal':
      return `/dashboard?action=withdraw&private=${target.id}`
    case 'transfer':
      return `/dashboard?action=withdraw&transfer=${target.id}`
    case 'position':
      return `/dashboard?action=swap&position=${target.id}&mode=${target.mode}`
    case 'swap':
      return `/dashboard?action=swap&swap=${target.id}`
    case 'conversion':
      return `/dashboard?action=swap&conversion=${target.id}`
    case 'strategy':
      return pathname === '/dashboard' ? `/dashboard?strategy=${target.id}` : `/strategies?id=${target.id}`
  }
}

/** a flow still running: a green ring turning at the chip's left (John, Nov 5 2026: "remove the percentage… just do a green spinning circle") */
function Running() {
  return <span className="size-3.5 shrink-0 animate-spin rounded-full border-2 border-accent border-t-transparent motion-reduce:animate-none" aria-hidden="true" />
}

/**
 * The queue of money in flight, docked at the bottom like a browser's
 * download bar (John, Oct 31 2026): deposits arriving, withdrawals paying
 * out, swaps, buys, sells and strategy orders running. It shows only while
 * something is moving; a chip opens its flow's progress; whatever leaves
 * the queue stays a few seconds more as a finished chip, with a check or a
 * cross and its bar draining (John, Nov 1 2026: "remove the separated
 * snackbars; let it hang in there for 3 seconds when done"). A running chip
 * shows a turning ring, no fill (John, Nov 5 2026). Notices, what
 * used to be toasts, are chips here too (John, Nov 5 2026: "every snack bar
 * should just be converted into a progress item"): a result or a refusal
 * with its own mark, gone after its few seconds or a click.
 *
 * The server says what is moving (src/api/progress.ts, one read with no
 * caps and every rule in one place with its tests); this bar only draws it.
 * The activity feed reads it again the moment a record changes, so the poll
 * is the backstop (John, Nov 1 2026: "make everything snappier and not poll
 * based").
 */
export function ActivityBar() {
  const { pathname } = useLocation()
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const invalidatePortfolio = useInvalidatePortfolio()
  const [busy, setBusy] = useState(false)
  const progress = useQuery({ ...trpc.progress.list.queryOptions(), refetchInterval: busy ? 3_000 : 15_000, refetchOnWindowFocus: true })
  const flows = useMemo(() => progress.data?.flows ?? [], [progress.data])
  const notices = useNotices()
  // what a dialog announced the moment its button was pressed, drawn until the server lists the flow (John, Nov 5 2026)
  const announced = useAnnouncedFlows()

  // the balance watcher stays quiet about what is moving here
  useEffect(() => {
    noteOwnActivity(flows.flatMap((flow) => flow.symbols))
    setBusy(flows.length > 0 || announced.length > 0)
  }, [flows, announced.length])

  // an announced flow goes once the server lists it, which takes its place in the same paint, or once a read that answered
  // a second after the server was told does not list it (over already, or never tracked)
  const listed = (target: Target) => flows.some((flow) => flow.target.kind === target.kind && flow.target.id === target.id)
  useEffect(() => {
    if (!progress.data) return
    for (const flow of announced) {
      if (flow.target === null) continue
      if (listed(flow.target) || (flow.settledAt !== null && progress.dataUpdatedAt > flow.settledAt + 1_000)) withdrawAnnounced(flow.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flows, announced, progress.dataUpdatedAt])

  // when the bar first saw each chip: the oldest sits leftmost, the newest rightmost, and a chip never moves once placed
  // (John, Nov 5 2026: "sometimes they jump to the front"); the server lists flows by kind, not by age
  const firstSeen = useRef<Map<string, number>>(new Map())
  const seenAt = (id: string) => {
    let at = firstSeen.current.get(id)
    if (at === undefined) {
      at = Date.now()
      firstSeen.current.set(id, at)
    }
    return at
  }
  // whatever left the queue since last time stays a few seconds as a finished chip, in its place, with its record's own verdict
  const [finished, setFinished] = useState<Array<{ id: string; title: TitlePart[]; outcome: 'ok' | 'failed'; at: number }>>([])
  const finish = (id: string, title: TitlePart[], outcome: 'ok' | 'failed') => {
    const at = firstSeen.current.get(id) ?? Date.now()
    setFinished((list) => [...list.filter((f) => f.id !== id), { id, title, outcome, at }])
    window.setTimeout(() => {
      setFinished((list) => list.filter((f) => f.id !== id))
      firstSeen.current.delete(id)
    }, FINISHED_MS)
  }
  const seen = useRef<Map<string, Flow>>(new Map())
  useEffect(() => {
    // a read that failed keeps the last flows on screen; only a read that answered can say a flow is gone
    if (!progress.data) return
    const now = new Map(flows.map((flow) => [flow.id, flow]))
    for (const [id, flow] of seen.current) {
      if (now.has(id)) continue
      // the record says how it ended; a flow that merely left the list (a custody switch) ends nothing
      void queryClient
        .fetchQuery({ ...trpc.progress.outcome.queryOptions({ id }), staleTime: 0 })
        .then((result) => {
          if (result.outcome === 'ok') {
            // a deposit's title names the token that actually arrived, not what the chip guessed (Nov 1 2026)
            finish(id, result.title ?? flow.title, 'ok')
            noteOwnActivity(flow.symbols)
            void invalidatePortfolio()
          } else if (result.outcome === 'failed') finish(id, flow.title, 'failed')
        })
        .catch(() => undefined)
    }
    seen.current = now
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flows])

  if (flows.length === 0 && announced.length === 0 && finished.length === 0 && notices.length === 0) return null
  return (
    <div className="shrink-0 border-t border-line bg-bg px-4 py-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] sm:px-6 md:pb-2 lg:px-8" role="status" aria-live="polite">
      <div className="mx-auto flex w-full max-w-6xl items-center gap-3 overflow-x-auto text-sm">
        {[
          ...announced
            .filter((flow) => flow.target === null || !listed(flow.target))
            .map((flow) => ({
              at: flow.at,
              node:
                flow.target ? (
                  <Link key={`announced-${flow.id}`} to={hrefOf(flow.target, pathname)} title="Starting…" className={`${CHIP} transition-colors hover:border-accent/60 hover:bg-surface-3`}>
                    <Running />
                    <span className="flex items-center gap-1 whitespace-nowrap font-medium">
                      <Title parts={flow.title} />
                    </span>
                  </Link>
                ) : (
                  <span key={`announced-${flow.id}`} title="Starting…" className={CHIP}>
                    <Running />
                    <span className="flex items-center gap-1 whitespace-nowrap font-medium">
                      <Title parts={flow.title} />
                    </span>
                  </span>
                ),
            })),
          ...flows.map((flow) => ({
            at: seenAt(flow.id),
            // the whole chip is the link (John, Oct 31 2026); the title alone shows, the stage is its tooltip (John, Nov 1 2026:
            // "I'll click on it if I want the further status"); a ring turns while it runs, no fill (John, Nov 5 2026)
            node: (
              <Link key={flow.id} to={hrefOf(flow.target, pathname)} title={flow.stage} className={`${CHIP} transition-colors hover:border-accent/60 hover:bg-surface-3`}>
                <Running />
                <span className="flex items-center gap-1 whitespace-nowrap font-medium">
                  <Title parts={flow.title} />
                </span>
              </Link>
            ),
          })),
          ...finished.map((f) => ({
            at: f.at,
            node: (
              <span key={`finished-${f.id}`} className={`${CHIP} ${f.outcome === 'ok' ? 'border-accent/50' : 'border-negative/50'}`}>
                <Drain ok={f.outcome === 'ok'} />
                {f.outcome === 'ok' ? <CheckCircle2 className="size-4 shrink-0 text-accent" aria-hidden="true" /> : <XCircle className="size-4 shrink-0 text-negative" aria-hidden="true" />}
                <span className="flex items-center gap-1 whitespace-nowrap font-medium">
                  <Title parts={f.title} />
                </span>
              </span>
            ),
          })),
        ]
          .sort((a, b) => a.at - b.at)
          .map((item) => item.node)}
        {notices.map((n) => (
          // a notice chip: its own mark or the kind's, the title, the reason beside it when there is one (the whole of it
          // as the tooltip); a click takes it away early
          <button
            key={`notice-${n.id}`}
            type="button"
            onClick={() => dismissNotice(n.id)}
            title={typeof n.body === 'string' ? n.body : undefined}
            className={`${CHIP} text-left ${n.kind === 'success' ? 'border-accent/50' : n.kind === 'error' ? 'border-negative/50' : ''}`}
          >
            <Drain ok={n.kind !== 'error'} ms={n.ms} />
            {n.icon ? (
              <span className="flex shrink-0 items-center" aria-hidden="true">
                {n.icon}
              </span>
            ) : n.kind === 'success' ? (
              <CheckCircle2 className="size-4 shrink-0 text-accent" aria-hidden="true" />
            ) : n.kind === 'error' ? (
              <XCircle className="size-4 shrink-0 text-negative" aria-hidden="true" />
            ) : (
              <Info className="size-4 shrink-0 text-muted" aria-hidden="true" />
            )}
            <span className="flex items-center gap-1 whitespace-nowrap font-medium">{n.title}</span>
            {n.body ? (
              <>
                <Sep />
                <span className="max-w-[20rem] truncate text-muted">{n.body}</span>
              </>
            ) : null}
          </button>
        ))}
      </div>
    </div>
  )
}

/** a finished or notice chip's bar: full, then draining over the time the chip stays */
function Drain({ ok, ms = FINISHED_MS }: { ok: boolean; ms?: number }) {
  const [started, setStarted] = useState(false)
  useEffect(() => {
    const id = window.setTimeout(() => setStarted(true), 30)
    return () => window.clearTimeout(id)
  }, [])
  return (
    <span className="absolute inset-x-0 bottom-0 h-0.5 bg-surface-3" aria-hidden="true">
      <span className={`block h-full ${ok ? 'bg-accent' : 'bg-negative'} transition-[width] ease-linear`} style={{ width: started ? '0%' : '100%', transitionDuration: `${ms}ms` }} />
    </span>
  )
}
